/**
 * SoleadLab 2.0 - ZoomableImage
 *
 * Generic pan+zoom image component.
 * - Mouse wheel to zoom in/out
 * - Click-drag to pan when zoomed
 * - Double-click to toggle fit ↔ 1:1
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface ZoomableImageProps {
  src: string;
  /** Full-resolution variant swapped in after a short dwell — Drive-proxied
   *  photos display the fast sized thumbnail first; zooming needs the
   *  original. Omit when `src` already IS full-res. */
  fullSrc?: string;
  alt?: string;
  className?: string;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  /** When provided, the zoom/pan transform wraps this content instead of a
   *  plain <img> (e.g. ThermalAnnotator's img + ROI canvas, which must scale
   *  as one unit). `src` still drives the zoom reset on photo change.
   *  `fullSrc` is ignored in this mode. */
  children?: React.ReactNode;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

/** Dwell before the full-res original downloads WITHOUT zoom intent. Long on
 *  purpose: fast browsing must cost thumbnails only — the ~5MB originals are
 *  what clog the browser's connection pool. Zooming fetches immediately. */
const FULL_RES_DWELL_MS = 1500;

export default function ZoomableImage({ src, fullSrc, alt = '', className = '', onError, children }: ZoomableImageProps) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [wantFull, setWantFull] = useState(false);
  const [showFull, setShowFull] = useState(false);

  // Progressive full-res upgrade, demand-driven: the original is fetched the
  // moment the user ZOOMS (intent = detail needed) or after a long dwell —
  // never during fast browsing. The in-flight download is CANCELED on
  // unmount/photo change (src = '' aborts it) so abandoned views don't pile
  // multi-MB fetches onto the browser's connection limit.
  useEffect(() => { setWantFull(false); setShowFull(false); }, [src]);
  useEffect(() => {
    if (scale > MIN_SCALE) setWantFull(true);
  }, [scale]);
  useEffect(() => {
    if (!fullSrc || wantFull || children) return;
    const t = setTimeout(() => setWantFull(true), FULL_RES_DWELL_MS);
    return () => clearTimeout(t);
  }, [fullSrc, wantFull, src, children]);
  useEffect(() => {
    if (!wantFull || !fullSrc || showFull || children) return;
    let alive = true;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => { if (alive) setShowFull(true); };
    img.src = fullSrc;
    return () => {
      alive = false;
      img.onload = null;
      img.src = '';
    };
  }, [wantFull, fullSrc, showFull, children]);
  const displaySrc = showFull && fullSrc ? fullSrc : src;
  const dragRef = useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale((prev) => {
      const next = prev * (e.deltaY < 0 ? 1.15 : 0.87);
      const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
      if (clamped <= MIN_SCALE) setTranslate({ x: 0, y: 0 }); // Reset pan when fully zoomed out
      return clamped;
    });
  }, []);

  // Attach wheel as a NON-passive native listener so preventDefault actually
  // works. React's onWheel is passive, so calling preventDefault there is
  // ignored and logs "Unable to preventDefault inside passive event listener"
  // on every wheel tick.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Reset zoom/pan whenever the image changes (e.g. switching to another
  // indication's photo) so each new image opens at fit, not at the previous
  // image's zoom level.
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [src]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= MIN_SCALE) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTx: translate.x,
        startTy: translate.y,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        setTranslate({
          x: dragRef.current.startTx + (ev.clientX - dragRef.current.startX),
          y: dragRef.current.startTy + (ev.clientY - dragRef.current.startY),
        });
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [scale, translate]
  );

  const handleDoubleClick = useCallback(() => {
    if (scale > MIN_SCALE) {
      setScale(MIN_SCALE);
      setTranslate({ x: 0, y: 0 });
    } else {
      setScale(3);
    }
  }, [scale]);

  return (
    <div
      ref={containerRef}
      className={`zoomable-image ${className}`}
      style={{
        overflow: 'hidden',
        cursor: scale > MIN_SCALE ? 'grab' : 'zoom-in',
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {children ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transformOrigin: 'center center',
            transition: dragRef.current ? 'none' : 'transform 0.15s ease-out',
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      ) : (
        <img
          // Keyed by the PHOTO (src prop), not displaySrc: switching photos
          // remounts the element so the previous photo never lingers while
          // the new one loads; the full-res swap reuses the element (no
          // blank flash mid-zoom).
          key={src}
          src={displaySrc}
          alt={alt}
          draggable={false}
          onError={onError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transformOrigin: 'center center',
            transition: dragRef.current ? 'none' : 'transform 0.15s ease-out',
            pointerEvents: 'none',
          }}
        />
      )}
      {scale > MIN_SCALE && (
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 10,
            padding: '1px 5px',
            borderRadius: 3,
            pointerEvents: 'none',
          }}
        >
          {Math.round(scale * 100)}%
        </div>
      )}
    </div>
  );
}
